import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminReports = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('daily'); // 'daily', 'monthly', 'custom'
  const [customRange, setCustomRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReportData();
  }, [selectedDate, reportType]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      let url = '';

      if (reportType === 'daily') {
        url = `https://hs.learninghub.ind.in/api/admin/reports/daily?date=${selectedDate}`;
      } else if (reportType === 'monthly') {
        const date = new Date(selectedDate);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        url = `https://hs.learninghub.ind.in/api/admin/reports/monthly?month=${month}&year=${year}`;
      } else if (reportType === 'custom') {
        url = `https://hs.learninghub.ind.in/api/admin/reports/custom?startDate=${customRange.start}&endDate=${customRange.end}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setReportData(data.report);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Error loading report data');
      setLoading(false);
    }
  };

  // ============================================
  // REPLACE exportToExcel FUNCTION IN adminreports.js
  // THIS VERSION GETS **REAL DATA FROM DATABASE**
  // ============================================

  const exportToExcel = async () => {
    if (!reportData) return;

    console.log('📊 Fetching REAL booking data from database...');

    // ==================== FETCH REAL BOOKING DATA ====================
    let bookingsData = [];

    try {
      // Determine which date/range to fetch
      let url = '';
      if (reportType === 'daily') {
        url = `https://hs.learninghub.ind.in/api/admin/bookings?date=${selectedDate}`;
      } else if (reportType === 'monthly') {
        const date = new Date(selectedDate);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        url = `https://hs.learninghub.ind.in/api/admin/bookings?month=${month}&year=${year}`;
      } else if (reportType === 'custom') {
        url = `https://hs.learninghub.ind.in/api/admin/bookings?startDate=${customRange.start}&endDate=${customRange.end}`;
      }

      // Fetch detailed bookings
      const response = await fetch(url);
      const data = await response.json();

      if (data.success && data.bookings && data.bookings.length > 0) {
        bookingsData = data.bookings;
        console.log(`✅ Fetched ${bookingsData.length} bookings from database`);
      } else {
        console.log('⚠️ No bookings found, using fallback');
      }
    } catch (error) {
      console.error('❌ Error fetching bookings:', error);
    }

    // ==================== CREATE EXCEL WORKBOOK ====================
    const workbook = XLSX.utils.book_new();
    const reportRows = [];

    // Row 1: Empty
    reportRows.push([]);

    // Row 2: HEADERS (BOLD)
    reportRows.push([
      'Sl',           // A
      'Date',         // B
      'Room No',      // C
      'Name of Main Pax',  // D
      'Chk In',       // E
      'Chk Out',      // F
      'Room Tarrif',  // G
      'Room Cost',    // H
      'Food Cost',    // I
      'Other',        // J
      'Total',        // K
      'GST',          // L
      'Total',        // M
      'Pay_CC',       // N
      'Pay_UPI',      // O
      'Pay Cash',     // P
      'Due'           // Q
    ]);

    // ==================== DATA ROWS WITH REAL DATA ====================
    let slNo = 1;

    if (bookingsData.length > 0) {
      // USE REAL BOOKING DATA FROM DATABASE
      bookingsData.forEach((booking) => {
        const rowNum = reportRows.length + 1;

        // Format dates properly
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-IN');
        };

        // Calculate nights
        const checkInDate = new Date(booking.check_in_date);
        const checkOutDate = new Date(booking.check_out_date);
        const nights = Math.max(1, Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)));

        // Room tariff per night
        const roomTariff = parseFloat(booking.price_per_night || booking.room_rate || 0);

        // Additional costs (you may need to add these fields to your database)
        const foodCost = parseFloat(booking.food_cost || 0);
        const otherCost = parseFloat(booking.other_cost || booking.service_charges || 0);

        // Get payment breakdown by mode
        const payCC = parseFloat(booking.pay_cc || 0);
        const payUPI = parseFloat(booking.pay_upi || 0);
        const payCash = parseFloat(booking.pay_cash || booking.advance_paid || 0);

        // If pay modes not available, use advance_paid in cash
        const totalPaid = payCC + payUPI + payCash;
        const actualCash = totalPaid === 0 ? parseFloat(booking.advance_paid || 0) : payCash;

        reportRows.push([
          slNo++,                                           // A: Sl No
          formatDate(booking.created_at || booking.booking_date),  // B: Date
          booking.room_number || '',                        // C: Room No
          booking.primary_guest_name || booking.guest_name || 'Guest',  // D: Name
          formatDate(booking.check_in_date),                // E: Chk In
          formatDate(booking.check_out_date),               // F: Chk Out
          roomTariff,                                       // G: Room Tarrif
          { f: `${roomTariff}*${nights}` },                // H: Room Cost FORMULA
          foodCost,                                         // I: Food Cost
          otherCost,                                        // J: Other
          { f: `SUM(H${rowNum}:J${rowNum})` },             // K: Total FORMULA
          { f: `K${rowNum}*5%` },                           // L: GST FORMULA (5%)
          { f: `K${rowNum}+L${rowNum}` },                   // M: Total with GST
          payCC,                                            // N: Pay_CC
          payUPI,                                           // O: Pay_UPI
          actualCash,                                       // P: Pay Cash
          { f: `M${rowNum}-SUM(N${rowNum}:P${rowNum})` }   // Q: Due FORMULA
        ]);
      });
    } else {
      // NO DATA - Show message row
      reportRows.push([
        '',
        '',
        '',
        'No bookings found for selected date/range',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        ''
      ]);
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(reportRows);

    // ==================== COLUMN WIDTHS (Exact as template) ====================
    worksheet['!cols'] = [
      { wch: 3 },      // A: Sl
      { wch: 10.14 },  // B: Date
      { wch: 9.71 },   // C: Room No
      { wch: 19 },     // D: Name of Main Pax
      { wch: 10.14 },  // E: Chk In
      { wch: 13 },     // F: Chk Out
      { wch: 12.29 },  // G: Room Tarrif
      { wch: 11.57 },  // H: Room Cost
      { wch: 10.86 },  // I: Food Cost
      { wch: 9.29 },   // J: Other
      { wch: 10.29 },  // K: Total
      { wch: 7.71 },   // L: GST
      { wch: 10.29 },  // M: Total
      { wch: 9.29 },   // N: Pay_CC
      { wch: 13 },     // O: Pay_UPI
      { wch: 10.29 },  // P: Pay Cash
      { wch: 9.86 }    // Q: Due
    ];

    // ==================== STYLING ====================
    // Header row (Row 2) - Bold with gray background
    const headerCells = ['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2', 'H2', 'I2', 'J2', 'K2', 'L2', 'M2', 'N2', 'O2', 'P2', 'Q2'];
    headerCells.forEach(cell => {
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true, name: 'Arial Unicode MS', sz: 10 },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: 'D9D9D9' } },
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      }
    });

    // Apply borders to all data cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = 2; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
        worksheet[cellAddress].s.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
    }

    // Add sheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Download
    const dateStr = reportData.date || new Date().toISOString().split('T')[0];
    const fileName = `City_Inn_Report_${dateStr}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    console.log(`✅ City Inn Report exported with ${bookingsData.length} real bookings!`);
  };

  // ============================================
  // END OF REPLACEMENT CODE
  // ============================================

  // ============================================
  // IMPORTANT: YOU ALSO NEED TO ADD THIS BACKEND ENDPOINT!
  // Add this to your admin.js backend file
  // ============================================

  /*
  // GET /api/admin/bookings - Get detailed bookings for export
  router.get('/bookings', async (req, res) => {
      try {
          const { date, month, year, startDate, endDate } = req.query;
          
          let query = `
              SELECT 
                  b.id,
                  b.booking_id as booking_code,
                  b.created_at as booking_date,
                  b.check_in_date,
                  b.check_out_date,
                  b.number_of_nights,
                  b.total_amount,
                  b.advance_paid,
                  b.balance_due,
                  r.room_number,
                  r.price_per_night,
                  g.name as primary_guest_name
              FROM bookings b
              LEFT JOIN rooms r ON b.room_id = r.id
              LEFT JOIN booking_guests bg ON b.id = bg.booking_id AND bg.is_primary = 1
              LEFT JOIN guests g ON bg.guest_id = g.id
              WHERE 1=1
          `;
          
          const params = [];
          
          if (date) {
              query += ` AND DATE(b.check_in_date) = ?`;
              params.push(date);
          } else if (month && year) {
              query += ` AND MONTH(b.check_in_date) = ? AND YEAR(b.check_in_date) = ?`;
              params.push(month, year);
          } else if (startDate && endDate) {
              query += ` AND b.check_in_date BETWEEN ? AND ?`;
              params.push(startDate, endDate);
          }
          
          query += ` ORDER BY b.check_in_date DESC`;
          
          const [bookings] = await promisePool.execute(query, params);
          
          // Get payment breakdown for each booking
          for (let booking of bookings) {
              const [payments] = await promisePool.execute(
                  `SELECT payment_mode, SUM(amount) as total
                   FROM payments
                   WHERE booking_id = ?
                   GROUP BY payment_mode`,
                  [booking.id]
              );
              
              booking.pay_cc = 0;
              booking.pay_upi = 0;
              booking.pay_cash = 0;
              
              payments.forEach(p => {
                  if (p.payment_mode === 'Card') booking.pay_cc = parseFloat(p.total);
                  else if (p.payment_mode === 'UPI') booking.pay_upi = parseFloat(p.total);
                  else if (p.payment_mode === 'Cash') booking.pay_cash = parseFloat(p.total);
                  else if (p.payment_mode === 'Internet Banking') booking.pay_upi += parseFloat(p.total);
              });
          }
          
          res.json({
              success: true,
              bookings: bookings
          });
          
      } catch (error) {
          console.error('Error fetching bookings:', error);
          res.status(500).json({
              success: false,
              message: 'Error fetching bookings',
              error: error.message
          });
      }
  });
  */


  // ============================================
  // END OF REPLACEMENT CODE
  // ============================================

  const exportToPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPosition = 20;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Hotel Management System', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    doc.setFontSize(16);
    doc.text('Daily Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Date: ${reportData.date || new Date().toISOString().split('T')[0]}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Overview Statistics
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Overview Statistics', 14, yPosition);
    yPosition += 8;

    const overviewData = [
      ['Total Revenue', `₹${reportData.overview?.totalRevenue?.toLocaleString('en-IN') || 0}`],
      ['Total Bookings', reportData.overview?.totalBookings || 0],
      ['Check-ins', reportData.overview?.checkIns || 0],
      ['Check-outs', reportData.overview?.checkOuts || 0],
      ['Occupancy Rate', `${reportData.overview?.occupancyRate || 0}%`],
      ['Average Rate', `₹${reportData.overview?.averageRate?.toLocaleString('en-IN') || 0}`],
      ['Total Guests', reportData.overview?.totalGuests || 0]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: overviewData,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] },
      margin: { left: 14, right: 14 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // Revenue Breakdown
    if (reportData.revenueBreakdown) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Revenue Breakdown', 14, yPosition);
      yPosition += 8;

      const revenueData = Object.entries(reportData.revenueBreakdown).map(([key, value]) => [
        key.replace(/([A-Z])/g, ' $1').trim(),
        `₹${value.toLocaleString('en-IN')}`
      ]);

      doc.autoTable({
        startY: yPosition,
        head: [['Category', 'Amount']],
        body: revenueData,
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    }

    // Room Type Performance
    if (reportData.roomTypeRevenue && reportData.roomTypeRevenue.length > 0) {
      // Check if new page needed
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Room Type Performance', 14, yPosition);
      yPosition += 8;

      const roomTypeData = reportData.roomTypeRevenue.map(room => [
        room.type,
        room.bookings,
        `₹${room.revenue.toLocaleString('en-IN')}`,
        `${room.occupancy}%`
      ]);

      doc.autoTable({
        startY: yPosition,
        head: [['Type', 'Bookings', 'Revenue', 'Occupancy']],
        body: roomTypeData,
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    }

    // Payment Methods
    if (reportData.paymentMethods && reportData.paymentMethods.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Methods Distribution', 14, yPosition);
      yPosition += 8;

      const paymentData = reportData.paymentMethods.map(payment => [
        payment.method,
        `₹${payment.amount.toLocaleString('en-IN')}`,
        payment.count
      ]);

      doc.autoTable({
        startY: yPosition,
        head: [['Method', 'Amount', 'Count']],
        body: paymentData,
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        margin: { left: 14, right: 14 }
      });
    }

    // Activities
    if (reportData.activities && reportData.activities.length > 0) {
      doc.addPage();
      yPosition = 20;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Today\'s Activities', 14, yPosition);
      yPosition += 8;

      const activitiesData = reportData.activities.map(activity => [
        activity.time,
        activity.type,
        activity.description,
        activity.amount > 0 ? `₹${activity.amount.toLocaleString('en-IN')}` : '-'
      ]);

      doc.autoTable({
        startY: yPosition,
        head: [['Time', 'Type', 'Description', 'Amount']],
        body: activitiesData,
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8 }
      });
    }

    // Footer on each page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Download
    const fileName = `Hotel_Report_${reportData.date || 'custom'}.pdf`;
    doc.save(fileName);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'check-in': return '🔓';
      case 'check-out': return '🔒';
      case 'service': return '🛎️';
      case 'payment': return '💳';
      case 'maintenance': return '🔧';
      default: return '📋';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'check-in': return '#10b981';
      case 'check-out': return '#ef4444';
      case 'service': return '#3b82f6';
      case 'payment': return '#8b5cf6';
      case 'maintenance': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const styles = {
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '2rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    titleSection: {
      flex: 1,
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#6b7280',
    },
    controlsSection: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    dateSelector: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      background: 'white',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    },
    dateInput: {
      border: 'none',
      outline: 'none',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#1f2937',
      cursor: 'pointer',
    },
    exportButtons: {
      display: 'flex',
      gap: '0.5rem',
    },
    exportButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.95rem',
    },
    excelButton: {
      background: '#10b981',
      color: 'white',
    },
    pdfButton: {
      background: '#ef4444',
      color: 'white',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      transition: 'transform 0.2s ease',
    },
    statIcon: {
      fontSize: '2rem',
      marginBottom: '0.5rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1f2937',
    },
    statChange: {
      fontSize: '0.75rem',
      marginTop: '0.5rem',
    },
    sectionGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    card: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#1f2937',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    barChart: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    barItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    barLabel: {
      width: '120px',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#4b5563',
    },
    barContainer: {
      flex: 1,
      height: '30px',
      background: '#f3f4f6',
      borderRadius: '6px',
      overflow: 'hidden',
      position: 'relative',
    },
    barFill: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: 'white',
      transition: 'width 0.3s ease',
    },
    barValue: {
      width: '120px',
      textAlign: 'right',
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#1f2937',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: '0.75rem',
      textAlign: 'left',
      borderBottom: '2px solid #e5e7eb',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    td: {
      padding: '0.75rem',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '0.9rem',
      color: '#1f2937',
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    activityList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    activityItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem',
      background: '#f9fafb',
      borderRadius: '8px',
      borderLeft: '4px solid',
      transition: 'all 0.2s',
    },
    activityIcon: {
      fontSize: '1.5rem',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'white',
      borderRadius: '50%',
      flexShrink: 0,
    },
    activityContent: {
      flex: 1,
    },
    activityTime: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    activityDescription: {
      fontSize: '0.9rem',
      color: '#1f2937',
      fontWeight: 500,
    },
    activityAmount: {
      fontSize: '1rem',
      fontWeight: 700,
      color: '#10b981',
    },
    loading: {
      textAlign: 'center',
      padding: '3rem',
      fontSize: '1.2rem',
      color: '#666',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          Loading report data...
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          No data available for selected date
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <h1 style={styles.title}>📊 Reports & Analytics</h1>
          <p style={styles.subtitle}>Comprehensive daily performance report</p>
        </div>

        <div style={styles.controlsSection}>
          {/* Date Selector */}
          <div style={styles.dateSelector}>
            <span>📅</span>
            <input
              type="date"
              style={styles.dateInput}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          {/* Export Buttons */}
          <div style={styles.exportButtons}>
            <button
              style={{ ...styles.exportButton, ...styles.excelButton }}
              onClick={exportToExcel}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              📊 Export Excel
            </button>
            <button
              style={{ ...styles.exportButton, ...styles.pdfButton }}
              onClick={exportToPDF}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              📄 Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div style={styles.statsGrid}>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statIcon}>💰</div>
          <div style={styles.statLabel}>Total Revenue</div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>
            ₹{reportData.overview?.totalRevenue?.toLocaleString('en-IN') || 0}
          </div>
          <div style={{ ...styles.statChange, color: '#10b981' }}>↑ Today's earnings</div>
        </div>

        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statIcon}>📋</div>
          <div style={styles.statLabel}>Total Bookings</div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>
            {reportData.overview?.totalBookings || 0}
          </div>
          <div style={{ ...styles.statChange, color: '#6b7280' }}>
            Check-ins: {reportData.overview?.checkIns || 0} • Check-outs: {reportData.overview?.checkOuts || 0}
          </div>
        </div>

        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statIcon}>🏨</div>
          <div style={styles.statLabel}>Occupancy Rate</div>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>
            {reportData.overview?.occupancyRate || 0}%
          </div>
          <div style={{ ...styles.statChange, color: '#10b981' }}>Room utilization</div>
        </div>

        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statIcon}>💵</div>
          <div style={styles.statLabel}>Average Rate</div>
          <div style={styles.statValue}>
            ₹{reportData.overview?.averageRate?.toLocaleString('en-IN') || 0}
          </div>
          <div style={{ ...styles.statChange, color: '#10b981' }}>Per booking</div>
        </div>

        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statIcon}>👥</div>
          <div style={styles.statLabel}>Total Guests</div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>
            {reportData.overview?.totalGuests || 0}
          </div>
          <div style={{ ...styles.statChange, color: '#6b7280' }}>Today's guests</div>
        </div>
      </div>

      {/* Revenue Breakdown & Room Performance */}
      <div style={styles.sectionGrid}>
        {/* Revenue Breakdown Chart */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>💰</span>
            Revenue Breakdown
          </div>
          <div style={styles.barChart}>
            {reportData.revenueBreakdown && Object.entries(reportData.revenueBreakdown).map(([key, value]) => {
              const percentage = reportData.overview?.totalRevenue > 0
                ? (value / reportData.overview.totalRevenue) * 100
                : 0;
              return (
                <div key={key} style={styles.barItem}>
                  <div style={styles.barLabel}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                  <div style={styles.barContainer}>
                    <div
                      style={{
                        ...styles.barFill,
                        width: `${percentage}%`,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      }}
                    >
                      {percentage > 20 && `₹${value.toLocaleString('en-IN')}`}
                    </div>
                  </div>
                  <div style={styles.barValue}>₹{value.toLocaleString('en-IN')}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Room Type Performance */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>🛏️</span>
            Room Type Performance
          </div>
          {reportData.roomTypeRevenue && reportData.roomTypeRevenue.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Bookings</th>
                  <th style={styles.th}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {reportData.roomTypeRevenue.map((room, index) => (
                  <tr key={index}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{room.type}</td>
                    <td style={styles.td}>{room.bookings}</td>
                    <td style={{ ...styles.td, fontWeight: 600, color: '#10b981' }}>
                      ₹{room.revenue.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              No room data available
            </div>
          )}
        </div>
      </div>

      {/* Payment Methods */}
      {reportData.paymentMethods && reportData.paymentMethods.length > 0 && (
        <div style={{ ...styles.card, marginBottom: '2rem' }}>
          <div style={styles.cardTitle}>
            <span>💳</span>
            Payment Methods Distribution
          </div>
          <div style={styles.barChart}>
            {reportData.paymentMethods.map((payment, index) => {
              const percentage = reportData.overview?.totalRevenue > 0
                ? (payment.amount / reportData.overview.totalRevenue) * 100
                : 0;
              const colors = ['#667eea', '#10b981', '#f59e0b', '#3b82f6'];
              return (
                <div key={index} style={styles.barItem}>
                  <div style={styles.barLabel}>{payment.method}</div>
                  <div style={styles.barContainer}>
                    <div
                      style={{
                        ...styles.barFill,
                        width: `${percentage}%`,
                        background: colors[index % colors.length],
                      }}
                    >
                      {percentage > 15 && `₹${payment.amount.toLocaleString('en-IN')}`}
                    </div>
                  </div>
                  <div style={styles.barValue}>
                    ₹{payment.amount.toLocaleString('en-IN')} ({payment.count})
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Today's Activities */}
      {reportData.activities && reportData.activities.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>📋</span>
            Today's Activities & Transactions
          </div>
          <div style={styles.activityList}>
            {reportData.activities.map((activity, index) => (
              <div
                key={index}
                style={{
                  ...styles.activityItem,
                  borderLeftColor: getActivityColor(activity.type),
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f9fafb'}
              >
                <div style={styles.activityIcon}>{getActivityIcon(activity.type)}</div>
                <div style={styles.activityContent}>
                  <div style={styles.activityTime}>{activity.time}</div>
                  <div style={styles.activityDescription}>{activity.description}</div>
                </div>
                {activity.amount > 0 && (
                  <div style={styles.activityAmount}>₹{activity.amount.toLocaleString('en-IN')}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;